<template>
  <Layout>
    <AlertQuota :quotaStatus="quotaStatus" />

    <PageHeader :title="title" :subTitle="subTitle">
      <template #afterTitle>
        <McButton
          variant="primary"
          :text="$t('global.addField')"
          icon="icon-fill_plus"
          :disabled="quotaStatus === 'empty'"
          :displayTooltip="true"
          :tooltipText="$t('routeOptimization.tooltip.disabledCreateRO')"
          tooltipPosition="left"
          @click="handleAdd"
        />
      </template>
    </PageHeader>

    <McTable
      :key="tableKey"
      class="route-optimization__table"
      withCard
      searchable
      :apiClient="apiClient"
      :fields="fields"
      :hasCheckbox="false"
      :activeFilters="activeFilters"
      :customHandlers="customHandlers"
    >
      <template #before-table>
        <div class="d-flex justify-content-between mb-3">
          <McInlineFilter
            :filterItems="inlineFilterItems"
            :activeKeys="activeFilters['status.name']"
            @update:activeKeys="handleChangeActiveFilters([{ key: 'status.name', values: $event }])"
          />
          <QuotaCounter @quotaStatus="handleQuotaStatus" />
        </div>
      </template>
      <template #cell(status)="{ value, item }">
        <div class="d-flex justify-content-between">
          <div>
            <span
              v-if="value?.color"
              class="__status-dots"
              :style="{ backgroundColor: value.color }"
            ></span>
            <span class="mc-text-status">{{ formattedStatus(value.raw_type) }}</span>
          </div>

          <McButton
            v-if="!['SUCCESS', 'CANCELED'].includes(value.raw_type) && false"
            size="sm"
            v-bind="{ ...actionItems[value.raw_type] }"
            :disabled="isLoading"
            class="mx-1"
            @click="handleClickAction(value.raw_type, item)"
          />
        </div>
      </template>

      <template #cell(created_on)="{ value }">
        <template>
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
      </template>

      <template #cell(expected_departure_on)="{ value }">
        <template>
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="mc-datetime">
            {{ formatIndexDate(value).time }}
          </div>
        </template>
      </template>
    </McTable>

    <ModalUsageLimit ref="modal-usage-limit-ref" :routeOptimizationUsage="routeOptimizationUsage" />
  </Layout>
</template>

<script>
import appConfig from '@/app.config'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McTable from '@/components/McTable'
import McInlineFilter from '@/components/McInlineFilter'

import ModalUsageLimit from './components/ModalUsageLimit'
import QuotaCounter from './components/QuotaCounter'
import AlertQuota from './components/QuotaCounter/AlertQuota.vue'

import { formatIndexDate } from '@/helpers/tzDate'
import * as RouteOptimization from '@/services/rest-api/lib/route-optimization/index.js'

import getFields from './lib/fields/ro-list-fields.js'
import getInlineFilterItems from './lib/constants/inline-filter-items.js'
import filterMappings from './lib/constants/filter-mappings'

export default {
  page: {
    title: 'Route Optimization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McTable,
    McInlineFilter,
    ModalUsageLimit,
    QuotaCounter,
    AlertQuota,
  },
  data() {
    return {
      tableKey: 1,
      isLoading: false,

      metadata: null,
      fetchPeriodically: null,

      params: {},
      activeFilters: {},

      customHandlers: {
        handleClickRow: this.handleClickRow,
      },

      routeOptimizationUsage: {},

      quotaStatus: 'available',
      quotaAlert: null,
    }
  },
  computed: {
    title() {
      return this.$t('routeOptimization.title')
    },
    subTitle() {
      return this.$t('routeOptimization.description')
    },
    inlineFilterItems() {
      return getInlineFilterItems(this)
    },
    apiClient() {
      let getRouteOptimization = RouteOptimization.get(
        null,
        this.handleSetParams,
        this.handleSetMetadata
      )

      if (Object.keys(this.activeFilters).length > 0) {
        let queryParamFilters = []

        // mapping the activeFilters to be sent as query params (in Array)
        Object.keys(this.activeFilters).forEach((key) => {
          const filter = filterMappings[key]?.(key, this.activeFilters[key]) ?? undefined
          if (filter) {
            queryParamFilters.push(filter)
          }

          getRouteOptimization = RouteOptimization.get(
            queryParamFilters,
            this.handleSetParams,
            this.handleSetMetadata,
            this.handleSetCustomFields
          )
        })
      }

      return {
        get: getRouteOptimization,
      }
    },
    fields() {
      return getFields(this).filter((field) => field?.isShow !== false)
    },
    actionItems() {
      return {
        FAILED: {
          variant: 'secondary',
          text: this.$t('global.action.retry'),
          icon: 'icon-outline_refresh',
        },
        PENDING: {
          variant: 'secondary-red',
          text: this.$t('global.action.cancel'),
          icon: 'icon-outline_close',
        },
      }
    },
  },
  watch: {
    metadata(newValue) {
      if (this.fetchPeriodically !== null) {
        if (newValue.pending_count > 0) return

        this.clearTimeInterval()
        window.removeEventListener('focus', this.fetchDataPeriodically)
        window.removeEventListener('blur', this.clearTimeInterval)

        return
      }

      if (newValue.pending_count === 0) return

      this.fetchDataPeriodically()

      window.addEventListener('focus', this.fetchDataPeriodically)
      window.addEventListener('blur', this.clearTimeInterval)
    },
  },
  beforeDestroy() {
    if (this.fetchPeriodically === null) return

    this.clearTimeInterval()
    window.removeEventListener('focus', this.fetchDataPeriodically)
    window.removeEventListener('blur', this.clearTimeInterval)
  },
  methods: {
    formatIndexDate,

    fetchDataPeriodically() {
      this.fetchPeriodically = setInterval(() => {
        this.tableKey += 1
      }, 60000)
    },

    clearTimeInterval() {
      clearInterval(this.fetchPeriodically)
      this.fetchPeriodically = null
    },

    async handleAdd() {
      const res = await RouteOptimization.getRouteOptimizationUsage()

      if (res.errors) {
        this.$mcToast.error(this.$t('routeOptimization.alert.generalError'))
        return
      }

      if (res.data?.usage >= res.data?.limit) {
        let remaining = res.data.limit - res.data.usage
        this.routeOptimizationUsage = {
          value: res.data.usage,
          remaining: remaining > 0 ? remaining : 0,
          limit: res.data.limit,
        }

        await this.$nextTick()
        this.$refs['modal-usage-limit-ref'].OpenModal()
        return
      }

      this.$router.push({ name: 'Route Optimization Create' })
    },

    handleClickRow(item) {
      let name = 'Route Optimization Detail'

      if (['FAILED', 'CANCELED'].includes(item.status.raw_type)) {
        name = 'Route Optimization Detail Setting'
      }

      this.$router.push({ name, params: { id: item.id } })
    },

    async handleCancelRO(item) {
      this.isLoading = true
      const res = await RouteOptimization.cancel(item.id)

      if (res.errors) {
        this.$mcToast.error(this.$t('routeOptimization.alert.inputFormatError'))
        return
      }

      this.$mcToast.warning(
        this.$t('routeOptimization.alert.optimizationCanceled', { data: item.number })
      )

      this.isLoading = false
      this.tableKey += 1
    },

    async handleRerun(item) {
      this.isLoading = true
      const res = await RouteOptimization.rerun(item.id)

      if (res.errors) {
        this.$mcToast.error(this.$t('routeOptimization.alert.inputFormatError'))
        return
      }

      this.tableKey += 1
      this.isLoading = false
    },

    handleClickAction(actionType, item) {
      if (actionType === 'PENDING') {
        this.handleCancelRO(item)
      } else if (actionType === 'FAILED') {
        this.handleRerun(item)
      }
    },

    handleSetParams(params) {
      this.params = params
    },

    handleSetMetadata(metadata) {
      this.metadata = metadata
    },

    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'created_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null, undefined, or empty string
        if (filter.values === null || filter.values === undefined || filter.values === '') return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })
    },

    formattedStatus(status) {
      return this.$t('routeOptimization.status.' + status.toLowerCase())
    },

    handleQuotaStatus(status) {
      this.quotaStatus = status
    },
  },
}
</script>

<style lang="scss" scoped>
.route-optimization {
  &__table {
    .__status-dots {
      display: inline-block;
      margin: auto;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      margin-right: 8px;
    }
  }
}
</style>
